import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Banner, Button, Card, Field, Input, Select, Spinner, Textarea, cn } from "@magarac/ui";
import { formatCents } from "@magarac/shared/money";
import type {
  BookingConfig,
  PublicBookingResult,
  PublicService,
} from "@magarac/shared/schemas/booking";
import { bookingApi, describeError } from "./api";
import { US_STATES, addDays, dayLabel, durationLabel, localDate, slotLabel } from "./format";
import { Turnstile } from "./Turnstile";

type Step = "service" | "where" | "time" | "details" | "done";
type Slot = { start: string; windowStart: string; windowEnd: string };
type Address = { line1: string; line2: string; city: string; state: string; postalCode: string };

const EMPTY_ADDRESS: Address = { line1: "", line2: "", city: "", state: "", postalCode: "" };
const DAYS_PER_LOAD = 14;

function slugFromPath(): string {
  return window.location.pathname.split("/").filter(Boolean)[0]?.toLowerCase() ?? "";
}

function addressInput(a: Address) {
  return {
    line1: a.line1.trim(),
    ...(a.line2.trim() ? { line2: a.line2.trim() } : {}),
    city: a.city.trim(),
    state: a.state,
    postalCode: a.postalCode.trim(),
    country: "US",
  };
}

/** SCH-19..SCH-22: book a visit in under 90 seconds on a phone, no account. */
export function App() {
  const [slug] = useState(slugFromPath);
  const [config, setConfig] = useState<BookingConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!slug) return;
    let live = true;
    bookingApi
      .config(slug)
      .then((c) => live && setConfig(c))
      .catch((err: unknown) => {
        if (!live) return;
        const e = describeError(err);
        if (e.message.startsWith("That is no longer available")) setNotFound(true);
        else setLoadError(e.message);
      });
    return () => {
      live = false;
    };
  }, [slug, attempt]);
  const load = () => {
    setLoadError(null);
    setAttempt((n) => n + 1);
  };
  useEmbedHeight();

  if (!slug || notFound)
    return (
      <Shell>
        <Card>
          <h1 className="mb-2 text-xl font-semibold">We could not find this booking page</h1>
          <p>Check the link you were given, or call the business to book.</p>
        </Card>
      </Shell>
    );
  if (loadError)
    return (
      <Shell>
        <Card>
          <h1 className="mb-2 text-xl font-semibold">This page did not load</h1>
          <p className="mb-4">{loadError}</p>
          <Button onClick={load}>Try again</Button>
        </Card>
      </Shell>
    );
  if (!config)
    return (
      <Shell>
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      </Shell>
    );
  if (!config.open) return <Closed config={config} />;
  return <Booking slug={slug} config={config} />;
}

function Shell({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <main className="mx-auto max-w-lg px-4 py-6">
      {title ? <h1 className="mb-4 text-2xl font-semibold">{title}</h1> : null}
      {children}
    </main>
  );
}

function CallLine({ phone }: { phone: string | null }) {
  if (!phone) return null;
  return (
    <p className="mt-3">
      Call{" "}
      <a className="font-medium text-brand-700 underline" href={`tel:${phone}`}>
        {phone}
      </a>
    </p>
  );
}

function Closed({ config }: { config: BookingConfig }) {
  return (
    <Shell title={config.businessName}>
      <Card>
        <p>{config.businessName || "This business"} is not taking online bookings right now.</p>
        <CallLine phone={config.businessPhone} />
      </Card>
    </Shell>
  );
}

function Booking({ slug, config }: { slug: string; config: BookingConfig }) {
  const tz = config.timezone;
  const [step, setStep] = useState<Step>("service");
  const [service, setService] = useState<PublicService | null>(null);
  const [address, setAddress] = useState<Address>(EMPTY_ADDRESS);
  const [locationId, setLocationId] = useState<string>(config.locations[0]?.id ?? "");
  const [slot, setSlot] = useState<Slot | null>(null);
  const [hold, setHold] = useState<{ holdId: string; expiresAt: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [result, setResult] = useState<PublicBookingResult | null>(null);

  const field = service?.mode !== "appointment";
  const where = field ? { address: addressInput(address) } : locationId ? { locationId } : {};

  const pickService = (s: PublicService) => {
    setService(s);
    setSlot(null);
    setNotice(null);
    if (s.mode === "appointment" && config.locations.length <= 1) setStep("time");
    else setStep("where");
  };

  if (step === "done" && result) return <Done config={config} result={result} />;

  return (
    <Shell title={config.businessName ? `Book with ${config.businessName}` : "Book online"}>
      <Progress step={step} field={field} />
      {notice ? <Banner tone="warn">{notice}</Banner> : null}
      {step === "service" ? (
        <ServiceStep config={config} onPick={pickService} />
      ) : step === "where" && service ? (
        field ? (
          <AddressStep
            address={address}
            onChange={setAddress}
            onBack={() => setStep("service")}
            onNext={() => {
              setNotice(null);
              setStep("time");
            }}
          />
        ) : (
          <LocationStep
            config={config}
            value={locationId}
            onChange={setLocationId}
            onBack={() => setStep("service")}
            onNext={() => setStep("time")}
          />
        )
      ) : step === "time" && service ? (
        <TimeStep
          slug={slug}
          config={config}
          service={service}
          where={where}
          previousHold={hold?.holdId}
          onBack={() => setStep(field || config.locations.length > 1 ? "where" : "service")}
          onHeld={(s, h) => {
            setSlot(s);
            setHold(h);
            setNotice(null);
            setStep("details");
          }}
        />
      ) : step === "details" && service && slot && hold ? (
        <DetailsStep
          slug={slug}
          config={config}
          service={service}
          slot={slot}
          hold={hold}
          address={field ? addressInput(address) : undefined}
          locationName={config.locations.find((l) => l.id === locationId)?.name ?? null}
          onBack={() => setStep("time")}
          onReleased={(message) => {
            setHold(null);
            setSlot(null);
            setNotice(message);
            setStep("time");
          }}
          onBooked={(r) => {
            setResult(r);
            setStep("done");
          }}
        />
      ) : null}
      <p className="mt-6 text-center text-sm text-muted">
        Times are in {tz.replace(/_/g, " ")} time.
      </p>
    </Shell>
  );
}

function Progress({ step, field }: { step: Step; field: boolean }) {
  const steps: Array<[Step, string]> = [
    ["service", "Service"],
    ["where", field ? "Address" : "Location"],
    ["time", "Time"],
    ["details", "Your details"],
  ];
  const at = steps.findIndex(([s]) => s === step);
  return (
    <ol className="mb-4 flex gap-2 text-sm" aria-label="Steps">
      {steps.map(([s, label], i) => (
        <li
          key={s}
          aria-current={s === step ? "step" : undefined}
          className={cn(
            "flex-1 border-t-4 pt-1",
            i <= at ? "border-brand-600 text-ink" : "border-line text-muted",
          )}
        >
          {label}
        </li>
      ))}
    </ol>
  );
}

function ServiceStep({
  config,
  onPick,
}: {
  config: BookingConfig;
  onPick: (s: PublicService) => void;
}) {
  return (
    <div>
      <h2 className="mb-3 text-lg font-semibold">What do you need?</h2>
      <ul className="space-y-3">
        {config.services.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => onPick(s)}
              className="block w-full rounded-lg border border-line bg-surface p-4 text-left hover:bg-brand-50 focus-visible:outline focus-visible:outline-2"
            >
              <span className="block text-base font-semibold">{s.name}</span>
              {s.description ? (
                <span className="block text-sm text-muted">{s.description}</span>
              ) : null}
              <span className="mt-1 block text-sm">
                {durationLabel(s.durationMin)}
                {s.priceCents !== null ? ` · ${formatCents(s.priceCents)}` : ""}
                {s.depositCents > 0 ? ` · ${formatCents(s.depositCents)} deposit to book` : ""}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <CallLine phone={config.businessPhone} />
    </div>
  );
}

function AddressStep({
  address,
  onChange,
  onBack,
  onNext,
}: {
  address: Address;
  onChange: (a: Address) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof Address) => (e: { target: { value: string } }) =>
    onChange({ ...address, [k]: e.target.value });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (
      !address.line1.trim() ||
      !address.city.trim() ||
      !address.state ||
      !address.postalCode.trim()
    )
      return setError("Fill in the street, city, state, and ZIP code.");
    if (!/^\d{5}(-\d{4})?$/.test(address.postalCode.trim()))
      return setError("Enter a 5-digit ZIP code.");
    setError(null);
    onNext();
  };
  return (
    <form onSubmit={submit} noValidate>
      <h2 className="mb-3 text-lg font-semibold">Where is the work?</h2>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <Field id="b-line1" label="Street address">
        <Input
          id="b-line1"
          autoComplete="address-line1"
          value={address.line1}
          onChange={set("line1")}
        />
      </Field>
      <Field id="b-line2" label="Apartment or unit (optional)">
        <Input
          id="b-line2"
          autoComplete="address-line2"
          value={address.line2}
          onChange={set("line2")}
        />
      </Field>
      <Field id="b-city" label="City">
        <Input
          id="b-city"
          autoComplete="address-level2"
          value={address.city}
          onChange={set("city")}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field id="b-state" label="State">
          <Select
            id="b-state"
            autoComplete="address-level1"
            value={address.state}
            onChange={set("state")}
          >
            <option value="">Choose</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="b-zip" label="ZIP code">
          <Input
            id="b-zip"
            inputMode="numeric"
            autoComplete="postal-code"
            value={address.postalCode}
            onChange={set("postalCode")}
          />
        </Field>
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="secondary" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" className="flex-1">
          See open times
        </Button>
      </div>
    </form>
  );
}

function LocationStep({
  config,
  value,
  onChange,
  onBack,
  onNext,
}: {
  config: BookingConfig;
  value: string;
  onChange: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <div>
      <h2 className="mb-3 text-lg font-semibold">Which location?</h2>
      <ul className="mb-4 space-y-2">
        {config.locations.map((l) => (
          <li key={l.id}>
            <label className="flex items-start gap-3 rounded-lg border border-line bg-surface p-3">
              <input
                type="radio"
                name="location"
                className="mt-1"
                checked={value === l.id}
                onChange={() => onChange(l.id)}
              />
              <span>
                <span className="block font-medium">{l.name}</span>
                {l.addressLine ? (
                  <span className="block text-sm text-muted">{l.addressLine}</span>
                ) : null}
              </span>
            </label>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Button variant="secondary" onClick={onBack}>
          Back
        </Button>
        <Button className="flex-1" disabled={!value} onClick={onNext}>
          See open times
        </Button>
      </div>
    </div>
  );
}

function TimeStep({
  slug,
  config,
  service,
  where,
  previousHold,
  onBack,
  onHeld,
}: {
  slug: string;
  config: BookingConfig;
  service: PublicService;
  where: Record<string, unknown>;
  previousHold: string | undefined;
  onBack: () => void;
  onHeld: (s: Slot, h: { holdId: string; expiresAt: string }) => void;
}) {
  const tz = config.timezone;
  const [from, setFrom] = useState(config.firstDate);
  const [loaded, setLoaded] = useState<{ key: string; slots: Slot[] | null; error: string | null }>(
    { key: "", slots: null, error: null },
  );
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<{ key: string; day: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const whereKey = JSON.stringify(where);
  const to =
    addDays(from, DAYS_PER_LOAD - 1) > config.lastDate
      ? config.lastDate
      : addDays(from, DAYS_PER_LOAD - 1);

  // Results are kept with the request they answer, so a stale answer never shows.
  const requestKey = `${service.id}|${from}|${to}|${whereKey}|${reload}`;
  useEffect(() => {
    let live = true;
    bookingApi
      .slots(slug, { serviceId: service.id, from, to, ...JSON.parse(whereKey) })
      .then((r) => live && setLoaded({ key: requestKey, slots: r.slots, error: null }))
      .catch(
        (err: unknown) =>
          live && setLoaded({ key: requestKey, slots: null, error: describeError(err).message }),
      );
    return () => {
      live = false;
    };
  }, [slug, service.id, from, to, whereKey, requestKey]);
  const current = loaded.key === requestKey ? loaded : null;
  const slots = current?.slots ?? null;
  const loadError = current?.error ?? null;

  const byDay = useMemo(() => {
    const m = new Map<string, Slot[]>();
    for (const s of slots ?? []) {
      const d = localDate(s.start, tz);
      (m.get(d) ?? m.set(d, []).get(d)!).push(s);
    }
    return m;
  }, [slots, tz]);

  const firstDay = slots?.[0] ? localDate(slots[0].start, tz) : null;
  const day = picked && picked.key === requestKey ? picked.day : firstDay;
  const setDay = (d: string) => setPicked({ key: requestKey, day: d });

  const needsCheck = Boolean(config.turnstileSiteKey);
  const pick = async (s: Slot) => {
    if (needsCheck && !token) return setError("One moment: we are checking that you are a person.");
    setBusy(s.start);
    setError(null);
    try {
      const h = await bookingApi.hold(slug, {
        serviceId: service.id,
        start: s.start,
        ...(where as object),
        ...(previousHold ? { replaces: previousHold } : {}),
        ...(token ? { turnstileToken: token } : {}),
      });
      onHeld(s, h);
    } catch (err) {
      const e = describeError(err);
      setError(e.message);
      if (e.pickAgain) setReload((n) => n + 1);
    } finally {
      setBusy(null);
    }
  };

  const later = addDays(to, 1);
  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold">Pick a time</h2>
      <p className="mb-3 text-sm text-muted">
        {service.name}
        {service.windowMin > 0 ? ": we arrive within the window you pick." : "."}
      </p>
      {error || loadError ? <Banner tone="danger">{error ?? loadError}</Banner> : null}
      {needsCheck ? <Turnstile siteKey={config.turnstileSiteKey!} onToken={setToken} /> : null}
      {slots === null && !loadError ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : slots && slots.length === 0 ? (
        <Card>
          <p>
            No open times between {dayLabel(from)} and {dayLabel(to)}.
          </p>
          <CallLine phone={config.businessPhone} />
        </Card>
      ) : slots ? (
        <>
          <div role="group" aria-label="Day" className="mb-3 flex gap-2 overflow-x-auto pb-1">
            {[...byDay.keys()].map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={d === day}
                onClick={() => setDay(d)}
                className={cn(
                  "min-h-11 shrink-0 rounded-lg border px-3 text-sm",
                  d === day ? "border-brand-600 bg-brand-600 text-white" : "border-line bg-surface",
                )}
              >
                {dayLabel(d)}
              </button>
            ))}
          </div>
          <ul className="grid grid-cols-2 gap-2">
            {(day ? (byDay.get(day) ?? []) : []).map((s) => (
              <li key={s.start}>
                <Button
                  variant="secondary"
                  className="w-full"
                  busy={busy === s.start}
                  disabled={busy !== null}
                  onClick={() => void pick(s)}
                >
                  {slotLabel(s, tz)}
                </Button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="secondary" onClick={onBack}>
          Back
        </Button>
        {from > config.firstDate ? (
          <Button variant="ghost" onClick={() => setFrom(config.firstDate)}>
            Earliest dates
          </Button>
        ) : null}
        {later <= config.lastDate ? (
          <Button variant="ghost" onClick={() => setFrom(later)}>
            Later dates
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function useCountdown(until: string): number {
  const [left, setLeft] = useState(() => Date.parse(until) - Date.now());
  useEffect(() => {
    const t = setInterval(() => setLeft(Date.parse(until) - Date.now()), 1000);
    return () => clearInterval(t);
  }, [until]);
  return Math.max(0, left);
}

function DetailsStep({
  slug,
  config,
  service,
  slot,
  hold,
  address,
  locationName,
  onBack,
  onReleased,
  onBooked,
}: {
  slug: string;
  config: BookingConfig;
  service: PublicService;
  slot: Slot;
  hold: { holdId: string; expiresAt: string };
  address: ReturnType<typeof addressInput> | undefined;
  locationName: string | null;
  onBack: () => void;
  onReleased: (message: string) => void;
  onBooked: (r: PublicBookingResult) => void;
}) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const left = useCountdown(hold.expiresAt);
  const expired = left <= 0;
  useEffect(() => {
    if (expired && !busy)
      onReleased("We held that time for 5 minutes and it has been released. Pick a time again.");
  }, [expired, busy, onReleased]);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim()) return setError("Enter your first name.");
    if (form.phone.replace(/\D/g, "").length < 10)
      return setError("Enter a phone number with area code, so we can reach you about the visit.");
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return setError("That email address does not look right.");
    setBusy(true);
    setError(null);
    try {
      const r = await bookingApi.book(slug, {
        holdId: hold.holdId,
        firstName: form.firstName.trim(),
        ...(form.lastName.trim() ? { lastName: form.lastName.trim() } : {}),
        phone: form.phone.trim(),
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
        ...(address ? { address } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      });
      onBooked(r);
    } catch (err) {
      const d = describeError(err);
      if (d.pickAgain) return onReleased(d.message);
      setError(d.message);
    } finally {
      setBusy(false);
    }
  };

  const mm = Math.floor(left / 60_000);
  const ss = String(Math.floor((left % 60_000) / 1000)).padStart(2, "0");
  return (
    <form onSubmit={(e) => void submit(e)} noValidate>
      <Card className="mb-4">
        <p className="font-semibold">{service.name}</p>
        <p>
          {dayLabel(localDate(slot.start, config.timezone))}, {slotLabel(slot, config.timezone)}
        </p>
        {address ? (
          <p className="text-sm text-muted">{`${address.line1}, ${address.city}`}</p>
        ) : null}
        {locationName ? <p className="text-sm text-muted">{locationName}</p> : null}
        <p className="mt-2 text-sm text-muted" aria-live="polite">
          We are holding this time for {mm}:{ss}.
        </p>
      </Card>
      <h2 className="mb-3 text-lg font-semibold">Your details</h2>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <div className="grid grid-cols-2 gap-3">
        <Field id="b-first" label="First name">
          <Input
            id="b-first"
            autoComplete="given-name"
            value={form.firstName}
            onChange={set("firstName")}
          />
        </Field>
        <Field id="b-last" label="Last name">
          <Input
            id="b-last"
            autoComplete="family-name"
            value={form.lastName}
            onChange={set("lastName")}
          />
        </Field>
      </div>
      <Field id="b-phone" label="Mobile phone" hint="We text your confirmation and reminders here.">
        <Input
          id="b-phone"
          type="tel"
          autoComplete="tel"
          value={form.phone}
          onChange={set("phone")}
        />
      </Field>
      <Field id="b-email" label="Email (optional)">
        <Input
          id="b-email"
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={set("email")}
        />
      </Field>
      <Field id="b-notes" label="Anything we should know? (optional)">
        <Textarea
          id="b-notes"
          rows={3}
          maxLength={1000}
          value={form.notes}
          onChange={set("notes")}
        />
      </Field>
      {service.depositCents > 0 ? (
        <p className="mb-3 text-sm">
          A {formatCents(service.depositCents)} deposit is due to confirm; you can pay it right
          after booking.
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="button" variant="secondary" onClick={onBack} disabled={busy}>
          Back
        </Button>
        <Button type="submit" className="flex-1" busy={busy}>
          Book
        </Button>
      </div>
    </form>
  );
}

function Done({ config, result }: { config: BookingConfig; result: PublicBookingResult }) {
  return (
    <Shell title="You're booked">
      <Card>
        <p className="text-lg font-semibold">{result.serviceName}</p>
        <p className="mb-2">{result.when}</p>
        <p className="text-sm text-muted">
          Booking #{result.jobNumber} with {config.businessName}. We will send a confirmation and a
          reminder before the visit.
        </p>
        {result.paymentUrl && result.depositCents > 0 ? (
          <a
            href={result.paymentUrl}
            target="_top"
            className="mt-4 inline-flex min-h-11 w-full items-center justify-center rounded-lg bg-brand-600 px-4 font-medium text-white"
          >
            Pay the {formatCents(result.depositCents)} deposit
          </a>
        ) : null}
        <CallLine phone={config.businessPhone} />
      </Card>
    </Shell>
  );
}

/** Embedded on a business's site: tell the parent page how tall to make the frame. */
function useEmbedHeight() {
  useEffect(() => {
    if (window.parent === window) return;
    const send = () =>
      window.parent.postMessage(
        { type: "j360-booking:height", height: document.documentElement.scrollHeight },
        "*",
      );
    const ro = new ResizeObserver(send);
    ro.observe(document.body);
    send();
    return () => ro.disconnect();
  }, []);
}
